import React, { useEffect, useState } from 'react';
import { pastTenseStatus, TRANSFER_DETAILS, TRANSFER_DETAILS_V2_CONSTANTS, TransferDetailsInitialState, transferDetailsMicroStatus, transferMacrostatus, transferStepStatus } from '@/utils/constants';
import { Box, Button, Image, useDisclosure, Spinner, Tooltip } from '@chakra-ui/react';
import { keyType, TransferMicrostatus, TransferStatusWorkflow } from '../types';
import { getDocumentsRequiredDetails, getGenerateMasterProofGenerateLoading, getIsSkipStepLoading, getIsSkipStepSuccess, getTransferDetails } from '../ducks/selectors';
import { checkIsNFATransfer, checkIsTransferCompleted, getCardColor, getFormattedStatus, getProgressStatusAndDateTime, getStartAndCompleteDate, getStepImg, getStepperStatusImg, getSubStatusColorClass, showRemainingTime } from '../utils';
import { getDynamicDuration } from '@/utils/utils';
import { callToGenerateMasterProof, callToGetDocumentStatus, callToGetTransferDetails, callToGetTransferStatusWorkFlow, callToSkipStep, setIsRefreshingTransferStatus, setIsSkipStepSuccess } from '../ducks/slice';
import { useDispatch } from 'react-redux';
import { logAnalyticsEvent } from '@/utils/analyticsLogger';
import { analyticsEvents } from '@/utils/analyticsEvents';
import ViewEmailModal from './view-email-modal';
import AppConfig from '@/utils/app-config.mjs';

interface TransferStatusStepper {
    transferStatusWorkflow: TransferStatusWorkflow[],
    isDownloadTransferProofEnabled: boolean,
    version: number,
    isGlobalAdmin: boolean
    isGlobalOperations?: boolean
}

const TransferStatusStepperV2 = ({ transferStatusWorkflow, isDownloadTransferProofEnabled, version, isGlobalAdmin, isGlobalOperations }: TransferStatusStepper) => {
    const transferDetails: any = getTransferDetails() || TransferDetailsInitialState;
    // const fireArmType = transferDetails?.TransferDetails?.products ? transferDetails?.TransferDetails?.products[0] : null;
    const [subStatusOpenStatus, setSubStatusOpenStatus] = useState<any>({})
    const [transferDuration, setTransferDuration] = useState<any>(null)
    const isGenerateMasterProofLoading = getGenerateMasterProofGenerateLoading();
    const documentsRequiredDetails = getDocumentsRequiredDetails();
    const isSkipStepSuccess = getIsSkipStepSuccess();
    const isSkipStepLoading = getIsSkipStepLoading();

    const dispatch = useDispatch();
    const [viewEmailId, setViewEmailId] = useState<string>('')

    const formatResumeReason = (reason?: string): string => {
        if (!reason) return '';
        const reasonMap: Record<string, string> = {
            dealer_not_opted_in: 'Dealer not opt-in',
            required_documents_not_available: 'Required document not available',
            required_documents_missing: 'Required document not available',
            ez_check_failed: 'Ez-check failed'
        };
        if (reasonMap[reason]) return reasonMap[reason];
        return reason
            .split('_')
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };


    const {
        isOpen,
        onClose,
        onOpen,
    } = useDisclosure();

    useEffect(() => {
        if (transferStatusWorkflow && transferStatusWorkflow.length > 0) {
            const transferDates = getStartAndCompleteDate(transferStatusWorkflow)
            if (transferDates?.sDate !== null && transferDates?.eDate !== null) {
                const durationTime = getDynamicDuration(transferDates?.sDate, transferDates?.eDate)
                setTransferDuration(durationTime)
            }
        }
    }, [transferStatusWorkflow])

    const onClickRefreshBtn = () => {
        const transferId = transferDetails?.TransferDetails?.transferId;
        if (transferId) {
            dispatch(setIsRefreshingTransferStatus(true));
            dispatch(callToGetTransferStatusWorkFlow({ id: transferId as string }));
            dispatch(callToGetDocumentStatus({ id: transferDetails?.fflMasterDetails.licenseId, transferId: transferId as string }));
            dispatch(
                callToGetTransferDetails({
                    id: transferId as string,
                    isRefresh: true
                })
            );
        }
    };


    useEffect(() => {
        if (isSkipStepSuccess) {
            dispatch(setIsSkipStepSuccess(false));
            onClickRefreshBtn();
        }
    }, [isSkipStepSuccess])


    const getStatusText = (step: any, stepStatus: any) => {
        const statuses = version === 1 ? (pastTenseStatus[step.status] || transferDetailsMicroStatus[step.status as keyType<typeof transferDetailsMicroStatus>]) : transferDetailsMicroStatus[step.status as keyType<typeof transferDetailsMicroStatus>];
        let statusText = stepStatus === transferStepStatus.success ? getFormattedStatus(statuses, step) : getFormattedStatus(transferDetailsMicroStatus[step.status as keyType<typeof transferDetailsMicroStatus>], step);
        if (step?.status === TransferMicrostatus.transfer_resume_attempted) {
            const source = step?.metaData?.source;
            const sourceText = source === 'manual' ? 'manual' : source === 'cron' ? 'scheduled' : source;
            const reasonValues = Array.isArray(step?.metaData?.reasons)
                ? step.metaData.reasons
                : step?.metaData?.reason
                    ? [step.metaData.reason]
                    : [];
            const reasonText = reasonValues.map((reason: string) => formatResumeReason(reason)).filter(Boolean).join(', ');
            return (
                <>
                    <div>{`${transferDetailsMicroStatus.transfer_resume_attempted}${sourceText ? ` (${sourceText})` : ''}`}</div>
                    {reasonText ? <div>{`Reason: ${reasonText}`}</div> : null}
                </>
            );
        }
        let emailText = '';
        if (step.status === TransferMicrostatus.resent_email) {
            emailText = `(${step.metaData?.toEmail || step.metaData?.email})`
        } else if (step.metaData?.email && ((step.status === TransferMicrostatus.dealer_selected) && (step.status === TransferMicrostatus.customer_selected))) {
            emailText = `(${step.metaData?.email})`
        } else if(step.status === TransferMicrostatus.sms_sent_to_dealer && step?.metaData && Object.keys(step.metaData).includes('isSMSSent') && step.metaData.isSMSSent === false) {
            statusText = transferDetailsMicroStatus.transfer_sms_send_to_dealer_timeout
        } else if (step.status === TransferMicrostatus.email_not_found) {
            if(step?.metaData?.email) {
                emailText = `(marked by ${step?.metaData?.email}) on `
            } else if(step?.metaData?.actionPerformedBy === 'System') {
                emailText = `(schedule timeout) on `
            }
        }
        return (
            <>
                {`${statusText} ${emailText}`}
            </>
        )
    }

    const getStatusFormattedTime = (step: any) => {
        if (step && step?.metaData && step?.metaData?.schedulerTimeout && step?.metaData?.schedulerId) {
            const formattedTime = showRemainingTime(step);
            return formattedTime;
        }
        return '';
    }


    const onOpenSubStatus = (step: any, isOpen: boolean) => {
        setSubStatusOpenStatus({
            ...subStatusOpenStatus,
            [step.status]: {
                isOpen: isOpen,
            }
        })
    }

    const onClickGenerateMasterProof = () => {
        dispatch(callToGenerateMasterProof({
            transferId: transferDetails?.TransferDetails.transferId
        })
        );
    };

    const onOpenViewEmail = (meta: any) => {
        setViewEmailId(meta?.previewId)
        onOpen()
    }

    const onOpenReviewLink = (meta: any) => {
        if (meta && meta.documentType) {
            switch (meta.documentType) {
                case 'FFL':
                    if (transferDetails.TransferDetails.transferId) {
                        const linkUrl = `/admin/license-approval?transferId=${transferDetails.TransferDetails.transferId}`;
                        window.open(linkUrl, '_blank');
                    }
                    break;

                case 'LOA':
                    if (transferDetails.TransferDetails.transferId) {
                        const linkUrl = `/admin/loa-approval?transferId=${transferDetails.TransferDetails.transferId}`;
                        window.open(linkUrl, '_blank');
                    }
                    break;
                case 'SOT':
                    if (transferDetails.TransferDetails.transferId) {
                        const linkUrl = `/admin/sot-tax-stamps?transferId=${transferDetails.TransferDetails.transferId}`;
                        window.open(linkUrl, '_blank');
                    }
                    break;

                default:
                    break;
            }
        }

    }

    const getViewEmailElements = (meta: any) => {
        if (meta?.metaData?.previewId) {
            return (
                <><Box className="review_link" onClick={() => onOpenViewEmail(meta.metaData)}>View Email</Box></>
            )
        }
    }

    const getMicroStatusElements = (step: any, isInActiveStep: any) => {
        const isShowOlderStatus = step?.microStatus.length > 1;
        let newMicroStatus = [];
        if (step?.microStatus.length > 0) {
            newMicroStatus = step?.microStatus.filter((stepItem: any) => stepItem?.metaData?.type !== 'notify_email_on_dealer_change');
        }
        const latestStatus = newMicroStatus.length > 0 ? newMicroStatus[0] : null;
        let updatedMicroStatus = []
        if (newMicroStatus && newMicroStatus.length > 0) {
            updatedMicroStatus = newMicroStatus.slice(1);
        }
        const isTransferCompleted = checkIsTransferCompleted(transferStatusWorkflow)
        return (
            <>
                {(latestStatus && latestStatus !== null) && (
                    <Box className={`sub_status_box ${getSubStatusColorClass(latestStatus)}`}>
                        <Box className="sub_status_label">
                            {getStatusText(latestStatus, step.stepStatus)}
                            {getProgressStatusAndDateTime(latestStatus, isTransferCompleted ? transferDetails?.TransferDetails?.outboundTransferCompletionTimestamp : null, step)}
                            {latestStatus?.metaData?.reason && latestStatus.status === TransferMicrostatus.dealer_selected && (
                                    <Box className="transfer_flex_row" sx={{ marginTop: '0.5rem' }}>
                                        <Tooltip hasArrow placement="top" label={latestStatus?.metaData?.reason} color={'black'} bg={'white'}>
                                            <Box
                                                className="timer_text"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: '1.4',
                                                    maxHeight: '2.8em', // 2 lines * 1.4 line-height
                                                    wordBreak: 'break-word',
                                                    cursor: 'default'
                                                }}
                                            >Reason: {latestStatus?.metaData?.reason}</Box>
                                        </Tooltip>
                                    </Box>
                                    )} 
                        </Box>
                        {getStatusFormattedTime(latestStatus) != '' && (
                            <Box className="transfer_flex_row">
                                {latestStatus?.status !== TransferMicrostatus.email_bounced && 
                                <Image src="/images/ui/timer.svg" sx={{ marginRight: '5px' }} width={'12px'} height={'12px'} />}
                                <Box className="transfer_flex_row">
                                    <Box className="timer_text">{getStatusFormattedTime(latestStatus)}</Box>
                                </Box>
                            </Box>
                        )}
                        {getViewEmailElements(latestStatus)}
                        {latestStatus?.status === TransferMicrostatus.manual_review_pending && (isGlobalAdmin || isGlobalOperations) && (
                            <Box className="review_link" onClick={() => onOpenReviewLink(latestStatus.metaData)}>Review Now</Box>
                        )}
                        {(latestStatus?.status === TransferMicrostatus.document_uploaded && (isGlobalAdmin || isGlobalOperations)) && (
                            <Box className="review_link" onClick={() => onOpenReviewLink(latestStatus.metaData)}>View Document</Box>
                        )}
                    </Box>
                )}
                {isShowOlderStatus && (
                    <>
                        <Box className="older_status_wrapper" onClick={() => onOpenSubStatus(step, !subStatusOpenStatus[step?.status]?.isOpen)}>
                            <Box className="older_status_label">{(subStatusOpenStatus && subStatusOpenStatus[step?.status] && subStatusOpenStatus[step?.status]?.isOpen) ? 'Older statuses' : 'Show older statuses'}</Box>
                            {(subStatusOpenStatus && !subStatusOpenStatus[step?.status]?.isOpen) && (
                                <Image src="/images/ui/downArrow.svg" sx={{ marginRight: '5px' }} width={'12px'} height={'12px'} />
                            )}
                        </Box>
                        {(updatedMicroStatus.length > 0 && (subStatusOpenStatus && subStatusOpenStatus[step?.status] && subStatusOpenStatus[step?.status]?.isOpen)) && updatedMicroStatus.map((stepMicro: any, index: number) => (
                            <Box className={`sub_status_box ${getSubStatusColorClass(stepMicro)}`} key={`sub-status-${index}`}>
                                <Box className="sub_status_label">
                                    {getStatusText(stepMicro, step.stepStatus)}
                                    {getProgressStatusAndDateTime(stepMicro, isTransferCompleted ? transferDetails?.TransferDetails?.outboundTransferCompletionTimestamp : null, step)}
                                    {stepMicro?.metaData?.reason && stepMicro?.status === TransferMicrostatus.dealer_selected && (
                                    <Box className="transfer_flex_row" sx={{ marginTop: '0.5rem' }}>
                                        <Tooltip hasArrow placement="top" label={stepMicro?.metaData?.reason} color={'black'} bg={'white'}>

                                            <Box
                                                className="timer_text"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: '1.4',
                                                    maxHeight: '2.8em', // 2 lines * 1.4 line-height
                                                    wordBreak: 'break-word',
                                                    cursor: 'default'
                                                }}
                                            >Reason: {stepMicro?.metaData?.reason}</Box>

                                        </Tooltip>
                                    </Box>
                                    )} 
                                </Box>
                                <Box className="transfer_flex_row" sx={{ justifyContent: 'space-between' }}>
                                    {getStatusFormattedTime(stepMicro) != '' && (
                                        <Box className="transfer_flex_row">
                                            {stepMicro?.status !== TransferMicrostatus.email_bounced &&
                                                <Image src="/images/ui/timer.svg" sx={{ marginRight: '5px' }} width={'12px'} height={'12px'} />}
                                            <Box className="transfer_flex_row">
                                                <Box className="timer_text">{getStatusFormattedTime(stepMicro)}</Box>
                                            </Box>
                                        </Box>
                                    )}
                                    {getViewEmailElements(stepMicro)}
                                    {stepMicro?.status === TransferMicrostatus.manual_review_pending && (isGlobalAdmin || isGlobalOperations) && (
                                        <Box className="review_link" onClick={() => onOpenReviewLink(stepMicro.metaData)}>Review Now</Box>
                                    )}
                                    {(stepMicro?.status === TransferMicrostatus.document_uploaded && (isGlobalAdmin || isGlobalOperations)) && (
                                        <Box className="review_link" onClick={() => onOpenReviewLink(stepMicro.metaData)}>View Document</Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                        {(subStatusOpenStatus && subStatusOpenStatus[step?.status] && subStatusOpenStatus[step?.status]?.isOpen && !subStatusOpenStatus[step?.status]?.isCurrentStep) && (
                            <Box className="older_status_wrapper" onClick={() => onOpenSubStatus(step, false)}>
                                <Box className="older_status_label">Hide older statuses</Box>
                                <Image src="/images/ui/downArrow.svg" sx={{ marginRight: '5px', transform: 'rotate(180deg)' }} width={'12px'} height={'12px'} />
                            </Box>
                        )}
                    </>
                )}

            </>
        )

    }
    const onSkipStep = () => {
        dispatch(callToSkipStep({ transferId: transferDetails?.TransferDetails.transferId }));
    }

    
    const getSkipStepLink = (step: any) => {
        const isNFATransfer = checkIsNFATransfer(transferDetails?.TransferDetails?.products);
        const emailStatuses = ["email_available", "email_acquired"];
        const hasEmailMicro = step.microStatus.some((micro: any) => emailStatuses.includes(micro.status));
        const hasAcceptedTransfer = step.microStatus.some((micro: any) => micro.status === "accepted_transfer");
        const isBouncedMicro = step.microStatus.some((micro: any) => micro.status === "email_bounced");
        let hasEmailSkipStep = false;
        if (transferStatusWorkflow && transferStatusWorkflow.length > 0) {
            const emailStep = transferStatusWorkflow.find((transferStep: any) => transferStep.status === "initiation_email_sent");
            if (emailStep && emailStep.microStatus && emailStep.microStatus.length > 0) {
                hasEmailSkipStep = step.microStatus.some((micro: any) => micro.status === "skip_step_acquired");
            }
        }

        // Check if ALL required documents are available
        let allDocumentsAvailable = true;

        // Check NFA document if it's an NFA transfer
        if (isNFATransfer) {
            allDocumentsAvailable = allDocumentsAvailable && documentsRequiredDetails?.hasValidSOT;
        }

        // Check LOA document if it's an LOA transfer
        if (transferDetails?.isLOATransfer) {
            allDocumentsAvailable = allDocumentsAvailable && documentsRequiredDetails?.hasValidLOA;
        }

        // Check CFD document if it's a CFD workflow
        if (documentsRequiredDetails?.isCFDWorkflow) {
            allDocumentsAvailable = allDocumentsAvailable && documentsRequiredDetails?.cfd;
        }
        // Check FFL license
        allDocumentsAvailable = allDocumentsAvailable && transferDetails?.isCertified;

        if (transferMacrostatus.initiation_email_sent === transferMacrostatus[step?.status] &&
            step.stepStatus === 'in_progress' &&
            (!hasEmailMicro || isBouncedMicro) &&
            !hasEmailSkipStep &&
            allDocumentsAvailable) {
            return (
                <>
                    <Box className="current_dealer_link" sx={{ alignSelf: 'center', marginBottom: '10px' }} onClick={() => onSkipStep()}>
                        {isSkipStepLoading ? (
                            <Spinner
                                className={`preview-loader`}
                                thickness={'2px'}
                                speed="0.65s"
                                emptyColor={'gray.200'}
                                color="#0C1E46"
                                size={'md'}
                            />
                        ) : 'Skip this step'}
                    </Box>
                </>
            );
        }
        const isFirstTimeDelay = step?.microStatus?.find((micro:any) => micro.status==='first_time_confirmation_delay');
        console.log("isFirstTimeDelay : ", isFirstTimeDelay);
        console.log("COND : ", transferMacrostatus.waiting_dealer_action === transferMacrostatus[step?.status] &&
            step.stepStatus === 'in_progress' &&
            !hasEmailSkipStep &&
            (isFirstTimeDelay || !hasAcceptedTransfer && !documentsRequiredDetails?.isOptedIn) &&
            allDocumentsAvailable
        );
        if (transferMacrostatus.waiting_dealer_action === transferMacrostatus[step?.status] &&
            step.stepStatus === 'in_progress' &&
            !hasEmailSkipStep &&
            (isFirstTimeDelay || (!hasAcceptedTransfer && !documentsRequiredDetails?.isOptedIn)) &&
            allDocumentsAvailable
        ) {
            return (
                <Box className="current_dealer_link" sx={{ alignSelf: 'center', marginBottom: '10px' }} onClick={() => onSkipStep()}>
                    {isSkipStepLoading ? (
                        <Spinner
                            className={`preview-loader`}
                            thickness={'2px'}
                            speed="0.65s"
                            emptyColor={'gray.200'}
                            color="#0C1E46"
                            size={'md'}
                        />
                    ) : 'Skip this step'}
                </Box>
            );
        }

    };
    const getTransferStatusElements = (step: TransferStatusWorkflow) => {
        const currentStepClass = step && step?.startedAt && step?.finishedAt == null ? 'current-step' : '';
        const isInActiveStep = step && step?.startedAt == null && step?.finishedAt == null;
        const outboundCompletionTs = transferDetails?.TransferDetails?.outboundTransferCompletionTimestamp;
        const isTransferCompletedMoreThan5MinAgo = !!outboundCompletionTs && (Date.now() - new Date(outboundCompletionTs).getTime() > 5 * 60 * 1000);
        if (subStatusOpenStatus && !subStatusOpenStatus[step.status] && currentStepClass === 'current-step') {
            setSubStatusOpenStatus({
                ...subStatusOpenStatus,
                [step.status]: {
                    isOpen: currentStepClass === 'current-step',
                    isCurrentStep: true,
                },

            })
        }
console.log('transferDetails !!!!!!!!!!', transferDetails);


        return (
            <Box className={`stepper_step_details_wrapper ${getCardColor(step, transferDetails.TransferDetails)}`}>
                <Box className='transfer_duration_wrapper'>
                    <Image src={getStepImg(step, transferDetails.TransferDetails)} sx={{ marginBottom: '10px' }} width={'50px'} height={'50px'} />
                    {(transferMacrostatus.outbound_transfer_complete === transferMacrostatus[step.status] && transferDuration && !isInActiveStep) && (
                        <Box className="transfer_duration_time">Total time to complete: <Box sx={{ fontWeight: 700, color: '#2E2E2E', marginLeft: '4px' }}>{transferDuration}</Box></Box>
                    )}
                    {getSkipStepLink(step)}
                </Box>
                <Box className="process_label">
                    {/* {(step.stepStatus === transferStepStatus.success ?

                        (pastTenseStatus[step.status] && step.status === 'waiting_license_validation' ?
                            (fireArmType === 'NFASuppressor' ? pastTenseStatus['sot_tax_stamp_validated'] : pastTenseStatus[step.status] || transferMacrostatus[step.status]) :
                            pastTenseStatus[step.status] || transferMacrostatus[step.status]) :

                        step.status === 'waiting_license_validation' ?
                            (fireArmType === 'NFASuppressor' ?
                                (step.stepStatus !== transferStepStatus.failed ? transferMacrostatus['sot_tax_stamp_validated'] : pastTenseFailedStatus['sot_tax_stamp_validated'] || transferMacrostatus['sot_tax_stamp_validated']) :
                                (step.stepStatus !== transferStepStatus.failed ?
                                    transferMacrostatus[step.status] :
                                    pastTenseFailedStatus[step.status] || transferMacrostatus[step.status])) :
                            pastTenseFailedStatus[step.status] || transferMacrostatus[step.status])} */}
                    {transferMacrostatus[step.status]}
                </Box>
                {(transferDetails?.TransferDetails?.transfer_proof_path && isDownloadTransferProofEnabled && transferMacrostatus.outbound_transfer_complete === transferMacrostatus[step.status]) && (
                    <a
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', minWidth: '230px', marginTop: '10px', padding: '5px' }}
                        className="transfer-action-btn transfer-email-btn"
                        href={`${AppConfig.transferProofLink}/${transferDetails?.TransferDetails?.transfer_proof_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => { logAnalyticsEvent(analyticsEvents.downloadMasterProof) }}
                    >
                        <Image src="/images/ui/download-light.svg" width={'22px'} height={'22px'} sx={{ marginRight: '10px' }} />
                        {transferDetails?.TransferDetails?.transferType === 'inbound'
                            ? TRANSFER_DETAILS.DOWNLOAD_RECIEVER_MASTER_PROOF
                            : TRANSFER_DETAILS_V2_CONSTANTS.FFL_MASTER_TRANSFER_PROOF}
                    </a>
                )}
                {isDownloadTransferProofEnabled && transferMacrostatus.outbound_transfer_complete === transferMacrostatus[step.status] && transferDetails?.TransferDetails?.transfer_seller_packet &&  (
                <a
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', minWidth: '230px', marginTop: '10px', padding: '5px', lineHeight: '1.2' }}
                        className="transfer-action-btn transfer-email-btn"
                        href={`${AppConfig.transferProofLink}/${transferDetails?.TransferDetails?.transfer_seller_packet}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Image src="/images/ui/download-light.svg" width={'22px'} height={'22px'} sx={{ marginRight: '10px' }} />
                        {TRANSFER_DETAILS_V2_CONSTANTS.RECEIVING_DEALER_DOCUMENTS_LABEL}
                    </a>
                )}
                {
                    (transferMacrostatus?.outbound_transfer_complete === transferMacrostatus[step.status] && transferDuration && !isInActiveStep && (transferDetails?.TransferDetails?.transfer_proof_link || isTransferCompletedMoreThan5MinAgo)) && (
                        <Button
                            sx={{ minWidth: '230px', marginTop: '10px' }}
                            colorScheme="blue"
                            background="#ffffff"
                            color="#0C1E46"
                            border="1px solid #0C1E46"
                            padding="8px 11px"
                            onClick={onClickGenerateMasterProof}
                            isLoading={isGenerateMasterProofLoading}
                        >
                            {TRANSFER_DETAILS_V2_CONSTANTS.REFRESH_MASTER_TRANSFER_PROOF}
                        </Button>
                    )
                }
                {getMicroStatusElements(step, isInActiveStep)}
            </Box>
        )
    }

    return (
        <>
            {isOpen && viewEmailId && (
                <ViewEmailModal isOpen={isOpen} onClose={onClose} emailId={viewEmailId} />
            )}
            {transferStatusWorkflow.map((step, index) => (
                <Box className="transfer_details_content_stepper" key={`stepper_${index}`}>
                    <Image src={getStepperStatusImg(step, transferDetails.TransferDetails)} className="step-img" width={'22px'} height={'22px'} />
                    {getTransferStatusElements(step)}
                </Box>
            ))}
        </>
    )
}

export default TransferStatusStepperV2
